import { managers, profile } from '~api';
import User from '~models/User';
import { getField, updateField } from 'vuex-map-fields';
import { Handler } from '~services';
import router from '~router';
import i18n from '../i18n';

const SET_MANAGER = 'SET_MANAGER';
const RESET_MANAGER = 'RESET_MANAGER';
const LOADING = 'LOADING';

const state = {
    manager: { ...new User() },
    isLoading: false
};

const actions = {
    /**
     * Reset manager state and redirect to manager list
     * @param {function} commit
     */
    cancel({ commit }) {
        commit(RESET_MANAGER);
        router.push({ name: 'dashboard.managers' });
    },

    /**
     * Reset manager state
     * @param {function} commit
     */
    resetManager({ commit }) {
        commit(RESET_MANAGER);
    },

    editAccount() {
        this.$router.push({
            name: 'dashboard.managers.edit',
            params: { id: this.item.id }
        });
    },

    /**
     * Create manager account
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     */
    async create({ commit, state }) {
        const successMessage = i18n.t('managers.create.successMessage');

        commit(LOADING);

        const {
            role,
            email,
            fullName,
            phone,
            position,
            information
        } = state.manager;

        // Api request to create manager
        const resp = await managers.create({
            role,
            email,
            fullName,
            phone,
            position,
            information
        });

        if (resp.ok) {
            commit(RESET_MANAGER);
        }

        // Handle response
        Handler.handleResponse(resp, successMessage, '', 'dashboard.managers');

        commit(LOADING);

        return resp;
    },

    /**
     * Get Manager account
     * @param {function} commit
     * @param  {string} id
     */
    async read({ commit }, id) {
        commit(LOADING);

        // Api request get manager account by id
        const resp = await managers.read(id);

        if (resp.ok) {
            if (!resp.data.data) {
                resp.data.data = {
                    position: null,
                    information: null
                };
            }
            commit(SET_MANAGER, resp.data);
        } else {
            // Handle response if error
            Handler.handleResponse(resp);
        }

        commit(LOADING);

        return resp;
    },

    /**
     * Update Manager account
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async update({ commit, state }) {
        const successMessage = i18n.t('managers.edit.successMessage');

        const {
            id,
            role,
            email,
            fullName,
            phone,
            position,
            information
        } = state.manager;

        commit(LOADING);

        const resp = await managers.update(id, {
            role,
            email,
            fullName,
            phone,
            position,
            information
        });

        Handler.handleResponse(resp, successMessage, '', 'dashboard.managers');

        // Reset store manager if account deleted
        if (resp.ok) {
            commit(RESET_MANAGER);
        }

        commit(LOADING);

        return resp;
    },

    /**
     * Upload Avatar  account
     * @param data {object} data
     * @returns {Promise<any>}
     */
    async uploadAvatar({ commit }, data) {
        commit(LOADING);

        const successMessage = i18n.t('managers.edit.successMessageAvatar');
        const resp = await profile.uploadCoverPhoto(data);

        Handler.handleResponse(resp, successMessage, '');

        commit(LOADING);

        return resp;
    },

    /**
     * Upload Avatar  account
     * @param data {object} data
     * @returns {Promise<any>}
     */
    async removeAvatar({ commit }, id) {
        commit(LOADING);

        const successMessage = i18n.t(
            'managers.edit.successMessageAvatarDeleted'
        );
        const resp = await profile.removeCoverPhoto(id);

        Handler.handleResponse(resp, successMessage, '');

        commit(LOADING);

        return resp;
    },

    /**
     * Activate/Deactivate account
     * @param {function} commit
     * @param {object} state
     * @returns {Promise<void>}
     */
    async updateStatus({ commit, state }) {
        const { id, isActive } = state.manager;
        // Set success message based on value isActive
        let successMessage = isActive
            ? i18n.t(
                  'managers.edit.settings.deactivate.successMessageActivated'
              )
            : i18n.t(
                  'managers.edit.settings.deactivate.successMessageDeactivated'
              );

        commit(LOADING);

        // Api request on update status
        const resp = await managers.updateStatus(id, { isActive });

        // Handle response
        Handler.handleResponse(resp, successMessage);

        commit(LOADING);
        return resp;
    },

    /**
     * Reset account password
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     */
    async resetPassword({ commit, dispatch, state }) {
        const successMessage = i18n.t(
            'managers.edit.settings.reset.successMessage'
        );

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.reset'),
            { root: true }
        );

        if (!confirmed) return;

        commit(LOADING);

        // Api request on password reset
        const resp = await managers.resetPassword(state.manager.id);

        // Handle response
        Handler.handleResponse(resp, successMessage);

        commit(LOADING);
    },

    /**
     * Delete Manager account.
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     * @param {string} id
     * @returns {Promise<void>}
     */
    async deleteAccount({ commit, dispatch, state }, id) {
        let accountId = id ? id : state.manager.id;
        const successMessage = i18n.t(
            'managers.edit.settings.delete.successMessage'
        );

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.delete'),
            { root: true }
        );
        if (!confirmed) return;

        commit('LOADING');

        // Api request on delete account
        const resp = await managers.delete(accountId);

        // Handle response
        Handler.handleResponse(resp, successMessage, '', 'dashboard.managers');

        // Reset store manager if account deleted
        if (resp.ok) {
            commit(RESET_MANAGER);
        }

        commit(LOADING);

        return resp.ok;
    }
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,
    [SET_MANAGER](state, data) {
        state.manager = data;
    },
    [RESET_MANAGER](state) {
        state.manager = { ...new User() };
    },
    [LOADING](state) {
        state.isLoading = !state.isLoading;
    }
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    isLoading: state => state.isLoading,
    managerRole: state => state.manager.role
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
